import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, Loader2, ExternalLink, ArrowRight, Eye, Code2, MessageCircle, Share2, ArrowUpRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SandpackBuilder from '../components/SandpackBuilder';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SharedBuildPage() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('preview'); // preview | code
  const [transcriptOpen, setTranscriptOpen] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/share/${shareId}`);
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Share not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [shareId]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 text-neutral-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading shared build…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-50 text-neutral-700 px-6 text-center">
        <div className="text-4xl mb-3">🔍</div>
        <h1 className="text-2xl font-bold text-neutral-900">Share not found</h1>
        <p className="mt-2 text-neutral-500 max-w-md">This build may have been unshared, or the link is wrong.</p>
        <Link to="/build" className="mt-6">
          <Button className="rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white">
            Build your own <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>
    );
  }

  const hasFiles = data.files && Object.keys(data.files).length > 0;
  const userMsgs = (data.transcript || []).filter((m) => m.role === 'user');
  const firstPrompt = userMsgs[0]?.content || '';

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      {/* Header */}
      <header className="border-b border-neutral-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-3 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
            <img src="/logo.png" alt="Transformerus" className="w-8 h-8 object-contain" />
            <span className="tfu-logo text-[16px] tracking-tight hidden sm:inline">transformerus</span>
          </Link>
          <div className="hidden md:flex items-center gap-1.5 text-xs text-neutral-500">
            <span>/</span>
            <span className="text-neutral-700 font-medium truncate max-w-[260px]">{data.name}</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              onClick={copyLink}
              variant="outline"
              size="sm"
              data-testid="share-copy-link-btn"
              className="h-8 text-xs rounded-lg"
            >
              <Share2 className="w-3.5 h-3.5 mr-1.5" /> Copy link
            </Button>
            <Link to={`/build?prompt=${encodeURIComponent(firstPrompt || data.name)}`}>
              <Button size="sm" data-testid="share-remix-btn" className="h-8 text-xs rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Remix this
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero strip */}
      <section className="bg-gradient-to-br from-cyan-50 via-white to-rose-50 border-b border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-neutral-200 text-[11px] font-medium text-neutral-600 mb-3">
                <Sparkles className="w-3 h-3 text-cyan-500" /> Built with Transformerus by {data.owner_name}
              </div>
              <h1 className="font-display text-3xl lg:text-4xl font-bold text-neutral-900 tracking-tight">
                {data.name}
              </h1>
              {firstPrompt && (
                <p className="mt-2 text-neutral-600 text-sm max-w-2xl line-clamp-2 italic">
                  "{firstPrompt}"
                </p>
              )}
            </div>
            {data.live_url && (
              <a href={data.live_url} target="_blank" rel="noopener noreferrer" data-testid="share-open-live">
                <Button className="rounded-lg bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white">
                  Open live site <ExternalLink className="w-4 h-4 ml-1.5" />
                </Button>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Main split: chat transcript | preview */}
      <main className="flex-1 max-w-[1400px] mx-auto w-full px-6 lg:px-10 py-8 grid lg:grid-cols-[360px_1fr] gap-6 min-h-0">
        {/* Transcript */}
        <aside className="bg-white border border-neutral-200 rounded-2xl overflow-hidden flex flex-col max-h-[calc(100vh-220px)]">
          <button
            onClick={() => setTranscriptOpen((v) => !v)}
            className="px-4 py-3 border-b border-neutral-200 flex items-center gap-2 text-left hover:bg-neutral-50 transition-colors"
            data-testid="share-toggle-transcript"
          >
            <MessageCircle className="w-4 h-4 text-cyan-500" />
            <div className="text-sm font-semibold text-neutral-900">How it was built</div>
            <span className="ml-auto text-[11px] text-neutral-400">{(data.transcript || []).length} msgs</span>
          </button>
          {transcriptOpen && (
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {(data.transcript || []).map((m, i) => (
                <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {m.role === 'assistant' && (
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-300 to-sky-400 flex-shrink-0 flex items-center justify-center">
                      <Sparkles className="w-3 h-3 text-white" />
                    </div>
                  )}
                  <div
                    className={`max-w-[85%] rounded-xl px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-neutral-900 text-white'
                        : 'bg-neutral-50 border border-neutral-200 text-neutral-800'
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {(data.transcript || []).length === 0 && (
                <p className="text-xs text-neutral-400 text-center py-8">No transcript available.</p>
              )}
            </div>
          )}
        </aside>

        {/* Preview / Code */}
        <section className="bg-white border border-neutral-200 rounded-2xl overflow-hidden flex flex-col min-h-[600px]">
          <div className="h-12 border-b border-neutral-200 flex items-center px-3 gap-2">
            <div className="inline-flex rounded-lg border border-neutral-200 p-0.5 bg-neutral-50">
              <button
                onClick={() => setActiveView('preview')}
                data-testid="share-view-preview"
                className={`px-3 py-1.5 rounded-md text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                  activeView === 'preview' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-900'
                }`}
              >
                <Eye className="w-3.5 h-3.5" /> Preview
              </button>
              <button
                onClick={() => setActiveView('code')}
                data-testid="share-view-code"
                className={`px-3 py-1.5 rounded-md text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                  activeView === 'code' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-900'
                }`}
              >
                <Code2 className="w-3.5 h-3.5" /> Code
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0">
            {hasFiles ? (
              <SandpackBuilder files={data.files} entry={data.entry || '/App.js'} activeView={activeView} />
            ) : (
              <div className="h-full flex items-center justify-center text-neutral-400 text-sm">
                No files in this share.
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Footer CTA */}
      <section className="border-t border-neutral-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 flex items-center justify-between flex-wrap gap-4">
          <div className="min-w-0">
            <div className="font-display text-xl font-bold text-neutral-900">Want one of these for your idea?</div>
            <p className="text-sm text-neutral-500 mt-0.5">Describe what you want. Watch Transformerus build it. Deploy live in under a minute.</p>
          </div>
          <Link to="/build">
            <Button className="h-10 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="share-cta-build">
              Start building <ArrowUpRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
